<template>
  <div class="navbar">
    <div class="navbar-wrapper">
      <s-breadcrumb separator="/" class="navbar-breadcrumb">
        <s-breadcrumb-item
          v-for="item in navList"
          :key="item.name"
          @click.native="openLink(item.routeName)"
        >
          {{ $i18n(item.title) }}
        </s-breadcrumb-item>
      </s-breadcrumb>
      <div class="navbar-actions">
        <s-button
          class="help-button"
          type="primary"
          @click="handleNeedHelpClick"
        >
          {{ $i18n('Need Help?') }}
        </s-button>
        <s-tooltip
          :content="$i18n('Support Panel')"
          v-if="$hasPermission('PC.SupportPanel') && showSupportPanelValue"
        >
          <div class="switch-panel-icon" @click="showSupportPanelDrawer">
            <s-icon-introduction-outline class="navbar-icon-small" />
          </div>
        </s-tooltip>
        <noti-widget />
        <div class="fullscreen-button">
          <s-icon-fullscreen-outline @click="handleFullscreen" />
        </div>
        <export-center v-if="$hasPermission('PC.ReportCenter.ReportCenter')" />
        <div
          v-if="$hasPermission('PC.Settings.JumpWFM')"
          class="system-out-button mr10"
          @click="handleSystemOutToWFM"
        >
          <svg-icon class="system-out-icon" icon-class="system-out" />
          WFM
        </div>
        <!-- <div class="system-out-button" @click="handleSystemOut">
          <svg-icon class="system-out-icon" icon-class="system-out" />
          Old System
        </div> -->
        <s-dropdown size="small">
          <span>{{ lang }}</span>
          <s-dropdown-menu slot="dropdown">
            <s-dropdown-item
              @click="handleLanguage(item)"
              v-for="item in languages"
              :key="item.value"
            >
              {{ item.label }}
            </s-dropdown-item>
          </s-dropdown-menu>
        </s-dropdown>
        <SIconChevronDown class="navbar-chevron-icon" />

        <div class="avatar">
          <img :src="user.userInfo.header || defaultHeader" alt="user-avatar" />
        </div>

        <s-dropdown size="small">
          <span>{{ user.userInfo.display_name || user.userInfo.email }}</span>
          <template #dropdown>
            <s-dropdown-menu>
              <s-dropdown-item :disabled="true" @click="handleItemClick">
                {{ user.userInfo.email }}
              </s-dropdown-item>
              <s-dropdown-item @click.native="handleChangePassword">
                {{ $i18n('Change Password') }}
              </s-dropdown-item>
              <s-dropdown-item @click="handleLogoutClick">
                {{ $i18n('Logout') }}
              </s-dropdown-item>
            </s-dropdown-menu>
          </template>
        </s-dropdown>
        <SIconChevronDown class="navbar-chevron-icon" />
        <ChangePasswordDialog
          v-if="changePasswordDialogVisible"
          :onConfirm="onChangePasswordConfirm"
          :onCancel="onChangePasswordCancel"
          :userDetail="user.userInfo"
          :visible="changePasswordDialogVisible"
        />
      </div>
      <SwitchPanel v-if="switchPanelVisible" v-model="switchPanelVisible" />
    </div>
    <s-dialog
      :visible.sync="showHelpDialog"
      :show-default-footer="true"
      :title="$i18n('Raise a Ticket')"
      size="normal"
      :confirmLoading="createTicketLoading"
      :handle-cancel="handleHelpDialogCancel"
      :handle-confirm="handleHelpDialogConfirm"
      @mousedown.native.capture="handleHelpDialogMouseDown"
    >
      <s-form
        v-loading="ticketTypeLoading || ticketTypeConfigLoading"
        class="help-ticket-form"
        :inline="false"
        :model="helpTicketForm"
      >
        <s-form-item
          :label="$i18n('Ticket Type')"
          prop="ticket_type_id"
          :required="true"
          label-position="top"
        >
          <s-select
            v-model="helpTicketForm.ticket_type_id"
            clearable
            class="help-ticket-control"
            :disabled="ticketTypeLoading"
            :no-data-text="$i18n('No Ticket Type is available')"
          >
            <s-option
              v-for="item in ticketTypeOptions"
              :key="item.value"
              :value="item.value"
              :label="item.label"
            />
          </s-select>
        </s-form-item>
        <s-form-item
          v-for="field in visibleHelpTicketFields"
          :key="field.config.field_type"
          :prop="field.fieldKey"
          :required="field.required"
          label-position="top"
        >
          <template v-slot:label>
            <span class="help-ticket-field-label">
              <span>{{ getHelpTicketFieldDisplayLabel(field) }}</span>
              <s-tooltip
                v-if="isHelpTicketDeviceField(field)"
                :append-to-body="false"
                :content="
                  $i18n(
                    'Device ID will map to the task ID to keep record in WMS'
                  )
                "
              >
                <s-icon-help-outline class="help-ticket-field-help-icon" />
              </s-tooltip>
            </span>
          </template>
          <template v-if="isHelpTicketScanField(field)">
            <s-input
              :ref="getHelpTicketFieldRef(field)"
              v-loading="isHelpTicketSkuField(field) && helpTicketSkuLoading"
              v-model="helpTicketForm[field.fieldKey]"
              clearable
              class="help-ticket-control help-ticket-scan-control"
              :placeholder="$i18n('Scan or Input')"
              @keyup.enter.native="
                handleHelpTicketScanFieldConfirm(field, true)
              "
              @blur="handleHelpTicketScanFieldBlur(field, $event)"
            >
              <template v-slot:prefix>
                <img
                  :src="scanInputIcon"
                  alt=""
                  class="help-ticket-scan-icon"
                />
              </template>
            </s-input>
            <div
              v-if="isHelpTicketSkuField(field) && helpTicketSkuInfo"
              v-loading="helpTicketSkuLoading"
              class="help-ticket-sku-card"
            >
              <div class="help-ticket-sku-image">
                <img
                  v-if="helpTicketSkuInfo.image"
                  :src="helpTicketSkuInfo.image"
                  :alt="$i18n('SKU Image')"
                />
              </div>
              <div class="help-ticket-sku-detail">
                <s-tooltip :content="helpTicketSkuInfo.sku_id || '-'">
                  <div class="help-ticket-sku-id">
                    {{ helpTicketSkuInfo.sku_id || '-' }}
                  </div>
                </s-tooltip>
                <s-tooltip :content="helpTicketSkuInfo.sku_name || '-'">
                  <div class="help-ticket-sku-name">
                    {{ helpTicketSkuInfo.sku_name || '-' }}
                  </div>
                </s-tooltip>
                <div
                  v-if="helpTicketSkuInfo.expect_qty !== undefined"
                  class="help-ticket-sku-qty"
                >
                  {{ $i18n('Expect Qty') }}:
                  {{ helpTicketSkuInfo.expect_qty }}
                </div>
              </div>
            </div>
          </template>
          <s-textarea
            v-else
            v-model="helpTicketForm[field.fieldKey]"
            class="help-ticket-control help-ticket-remark-control"
            :placeholder="$i18n('Input')"
            :maxlength="1024"
            :show-limit="true"
            :auto-rows="{ minRows: 4, maxRows: 4 }"
            :use-default-width="true"
          />
        </s-form-item>
        <div
          v-if="
            helpTicketForm.ticket_type_id &&
            !ticketTypeConfigLoading &&
            selectedTicketTypeConfig &&
            !helpTicketFieldConfigs.length
          "
          class="help-dialog-message"
        >
          {{ $i18n('No extra information is required for this ticket type.') }}
        </div>
      </s-form>
    </s-dialog>
    <s-dialog
      :visible.sync="showTicketCreatedDialog"
      :show-default-footer="true"
      :title="$i18n('Help Request Submitted')"
      size="medium"
      :cancel-button-text="$i18n('Cancel Ticket')"
      :confirm-button-text="$i18n('Supervisor Confirm')"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      :handle-cancel="handleTicketCreatedDialogCancel"
      :handle-confirm="handleSupervisorConfirmClick"
    >
      <div class="help-dialog-message">
        {{ $i18n('Supervisor')
        }}<span class="help-dialog-assignees"
          >({{ formattedSupervisorAssignees || '-' }})</span
        >
        {{
          $i18n(
            'is on their way to confirm at your location. Please wait for supervisor to confirm to resume your activity'
          )
        }}
      </div>
    </s-dialog>
    <s-dialog
      :visible.sync="showSupervisorPasswordDialog"
      :show-default-footer="false"
      :title="$i18n('Supervisor Password')"
      width="414px"
      custom-class="supervisor-password-dialog-container"
      size="normal"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
    >
      <div class="supervisor-password-dialog">
        <div class="supervisor-password-label">
          <span class="supervisor-password-required">*</span>
          {{ $i18n('Supervisor Password') }}
        </div>
        <s-input
          v-model="supervisorPassword"
          type="password"
          :show-password="true"
          class="supervisor-password-input"
          :placeholder="$i18n('Input')"
          @keyup.enter.native="handleSupervisorPasswordApprove"
        />
      </div>
      <div slot="footer" class="supervisor-password-footer">
        <s-button
          class="supervisor-password-reject"
          :disabled="supervisorPasswordLoading"
          @click="handleSupervisorPasswordReject"
        >
          {{ $i18n('Reject') }}
        </s-button>
        <s-button
          class="supervisor-password-approve"
          :loading="supervisorPasswordLoading"
          @click="handleSupervisorPasswordApprove"
        >
          {{ $i18n('Approve') }}
        </s-button>
      </div>
    </s-dialog>
    <s-dialog
      :visible.sync="showLeaveWarningDialog"
      :show-default-footer="true"
      :title="$i18n('Do not leave the current page')"
      size="medium"
      :confirm-button-text="$i18n('Leave anyway')"
      :cancel-button-text="$i18n('OK')"
      :close-on-click-modal="false"
      :handle-cancel="handleStayOnPage"
      :handle-confirm="handleLeaveAnyway"
    >
      <div class="leave-warning-message">
        {{
          $i18n(
            'Leave the current page will affect the supervisor verification activity. Please stay at this page until supervisor comes and verifies.'
          )
        }}
      </div>
    </s-dialog>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { State } from 'vuex-class';
import { logOut } from '@/api/basic/user';
import { Route } from 'vue-router';
import {
  EventBus,
  getCurrentLocale,
  getTranslateJSon,
  setLocale,
} from '@/i18n';
import { getSelectOptions, safeGet } from '@/utils/safeGet';
import { tspLanguageMap } from '@/i18n/locales';
import { getEnv, getWFMSystemUrl } from '@/utils/getEnv';
import defaultHeader from '@/assets/default-header.png';
import scanInputIcon from '@/assets/icons/scan-black.svg';
import { checkLoginSwitch, getTobURL } from '@/utils/tobLogin';
import ChangePasswordDialog from './change-password-dialog.vue';
import { resetMyPwd } from '@/api/system-config/userlist';
import TranslateReport from '@/utils/translateReport';
import { ExportCenter } from '@/components/common-export';
import NotiWidget from '@/components/noti-widget/index.vue';
import SwitchPanel from './support-panel/index.vue';
import { getCookie } from '@/utils/cookie';
import { reportEvent } from '@/utils/qms';
import LeavePageWarningService from '@/views/sales-outbound/checking-ui/services/LeavePageWarningService';
import {
  checkSupervisorPassword,
  createGenericTicket,
  getRaiseTicketTypeConfig,
  searchRaiseTicketType,
} from '@/api/work-ticket-management/generic-ticket';
import {
  doTicketAction,
  searchTicketActionList,
  searchTicketDetail,
} from '@/api/work-ticket-management/work-ticket-management';
import { WORK_TICKET_CREATED_EVENT } from '@/react-app/pages/work-ticket-management/work-ticket-management/constant';
import { searchSkuInfoDetail } from '@/api/wms-basic/sku-items';
import skuInputMappingFunction from '@/utils/skuInputMappingFunction';
import type {
  CreateGenericTicketRequest,
  CreateGenericTicketResponse,
  RaiseTicketTypeConfig,
  RaiseTicketTypeItem,
  TicketTypeFieldConfig,
} from '@/api/work-ticket-management/generic-ticket';

interface HelpTicketRenderedField {
  config: TicketTypeFieldConfig;
  fieldKey: string;
  label: string;
  required: boolean;
}

interface HelpTicketSkuInfo {
  sku_id: string;
  sku_name: string;
  image: string;
  expect_qty?: number;
}

const FIELD_REQUIREMENT_MANDATORY = 1;
const WORK_TICKET_ACTION_CODE_APPROVE = 2;
const WORK_TICKET_ACTION_CODE_REJECT = 3;
const WORK_TICKET_ACTION_CODE_CANCEL = 4;
const WORK_TICKET_ACTION_SOURCE_CREATOR_DEVICE = 2;
const TICKET_STATUS_DONE = 30;
const TICKET_STATUS_CANCEL = 40;
const TICKET_STATUS_REJECTED = 50;
const TICKET_STATUS_POLL_INTERVAL = 5000;
const HELP_TICKET_FIELD_REF_PREFIX = 'helpTicketField';

const normalizeEnumLabel = (value: string) =>
  String(value || '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();

const HELP_TICKET_FIELD_KEY_MAP: Record<string, string> = {
  'Location ID': 'location_id',
  'Station': 'station_id',
  'Dock No': 'dock_no',
  'Device ID': 'device_id',
  'SKU': 'sku_id',
  'Text Remark': 'text_remark',
};

@Component({
  components: {
    ChangePasswordDialog,
    ExportCenter,
    SwitchPanel,
    NotiWidget,
  },
})
export default class Navbar extends Vue {
  public defaultHeader = defaultHeader;
  public scanInputIcon = scanInputIcon;

  languageIcon = false;
  navList: object[] = [];
  setLocale = setLocale;
  getTranslateJSon = getTranslateJSon;
  getCurrentLocale = getCurrentLocale;
  getSelectOptions = getSelectOptions;
  tspLanguageMap = tspLanguageMap;
  lang = 'English';
  languages: any = [];
  backendLanguages: any;
  mockLanguages: any = {
    en: 'English',
    id: 'Bahasa Indonesia',
    ['zh-CN']: '简体中文',
    ['zh-TW']: '繁体中文',
    vi: 'Vietnamese',
    th: 'Thai',
    ['tl-PH']: 'Tagalog',
    ['ms-MY']: 'Bahasa Malayu',
    ['sp-MX']: 'Spanish',
  };
  $route: any;
  $router: any;

  changePasswordDialogVisible = false;
  showHelpDialog = false;
  showTicketCreatedDialog = false;
  showSupervisorPasswordDialog = false;
  switchPanelVisible = false;
  enableTobLogin = false;
  showSupportPanelValue = false;
  ticketTypeOptions: Array<{ label: string; value: string }> = [];
  ticketTypeLoading = false;
  ticketTypeConfigLoading = false;
  createTicketLoading = false;
  supervisorPasswordLoading = false;
  selectedTicketTypeConfig: RaiseTicketTypeConfig | null = null;
  helpTicketFieldConfigs: TicketTypeFieldConfig[] = [];
  ticketTypeConfigRequestSeq = 0;
  helpTicketSkuRequestSeq = 0;
  supervisorAssignees: string[] = [];
  supervisorPassword = '';
  createdTicketId = '';
  helpTicketSkuLoading = false;
  helpTicketSkuLoadedValue = '';
  helpTicketSkuInfo: HelpTicketSkuInfo | null = null;
  helpTicketSkuRequestPromise: Promise<void> | null = null;
  helpTicketForm: Record<string, string> = {
    ticket_type_id: '',
  };

  // 离开警告页面相关变量
  showLeaveWarningDialog = false;
  pendingAction: Function | null = null;
  unregisterRouterGuard: Function | null = null;
  ticketStatusPollingTimer: ReturnType<typeof setInterval> | null = null;
  currentProcess = 0;
  currentStage = 0;
  currentProcessLabel = '';
  currentStageLabel = '';

  @State user!: any;
  @State route!: any;

  get isVerificationInProgress() {
    return this.showTicketCreatedDialog || this.showSupervisorPasswordDialog;
  }

  async onChangePasswordConfirm(formData: any) {
    await resetMyPwd({
      new_pwd: formData.new_pwd,
      old_pwd: formData.old_pwd,
    });
    this.changePasswordDialogVisible = false;
  }

  onChangePasswordCancel() {
    this.changePasswordDialogVisible = false;
  }

  get formattedSupervisorAssignees() {
    const assignees = this.supervisorAssignees.slice(0, 3);
    if (this.supervisorAssignees.length > 3) {
      return `${assignees.join(' / ')} / ...`;
    }
    return assignees.join(' / ');
  }

  get needSupervisorVerification() {
    return (
      Number(this.selectedTicketTypeConfig?.require_supervisor_verification) ===
      1
    );
  }

  getEnumValue(enumName: string, label: string) {
    const enumMap =
      safeGet(this.$store.state.enums, `systemEnums.${enumName}`) || {};
    const normalizedLabel = normalizeEnumLabel(label);
    const matchedKey = Object.keys(enumMap).find(
      (key) => normalizeEnumLabel(key) === normalizedLabel
    );

    if (matchedKey) {
      return Number(enumMap[matchedKey]);
    }

    return 0;
  }

  getTicketProcessValue(label: string) {
    return this.getEnumValue('TicketProcess', label);
  }

  getTicketStageValue(label: string) {
    return this.getEnumValue('TicketStage', label);
  }

  getTicketFieldTypeLabel(fieldType: number) {
    const ticketFieldTypeEnum =
      safeGet(this.$store.state.enums, 'systemEnums.TicketFieldType') || {};
    return (
      Object.keys(ticketFieldTypeEnum).find(
        (key) => Number(ticketFieldTypeEnum[key]) === Number(fieldType)
      ) || ''
    );
  }

  get visibleHelpTicketFields(): HelpTicketRenderedField[] {
    return this.helpTicketFieldConfigs
      .map((config) => {
        const label = this.getTicketFieldTypeLabel(config.field_type);
        const fieldKey = HELP_TICKET_FIELD_KEY_MAP[label];
        if (!label || !fieldKey) {
          return null;
        }
        return {
          config,
          fieldKey,
          label,
          required: Number(config.requirement) === FIELD_REQUIREMENT_MANDATORY,
        };
      })
      .filter(Boolean) as HelpTicketRenderedField[];
  }

  isHelpTicketScanField(field: HelpTicketRenderedField) {
    return field.fieldKey !== 'text_remark';
  }

  isHelpTicketDeviceField(field: HelpTicketRenderedField) {
    return field.fieldKey === 'device_id';
  }

  isHelpTicketSkuField(field: HelpTicketRenderedField) {
    return field.fieldKey === 'sku_id';
  }

  getHelpTicketFieldRef(field: HelpTicketRenderedField) {
    return `${HELP_TICKET_FIELD_REF_PREFIX}-${field.fieldKey}`;
  }

  getHelpTicketFieldRefInstance(field: HelpTicketRenderedField) {
    const fieldRef = this.$refs[this.getHelpTicketFieldRef(field)];
    if (Array.isArray(fieldRef)) {
      return fieldRef[0] as any;
    }

    return fieldRef as any;
  }

  isHelpTicketFormTarget(target?: EventTarget | null) {
    if (!target || !(target instanceof Node)) {
      return false;
    }

    const formElement = this.$el.querySelector('.help-ticket-form');
    return Boolean(formElement?.contains(target));
  }

  handleHelpDialogMouseDown(event: MouseEvent) {
    this.isHelpTicketPointerDownInForm = this.isHelpTicketFormTarget(
      event.target
    );
  }

  focusHelpTicketField(field: HelpTicketRenderedField) {
    this.$nextTick(() => {
      const fieldRef = this.getHelpTicketFieldRefInstance(field);

      if (fieldRef?.focus) {
        fieldRef.focus();
        return;
      }

      const fieldElement = fieldRef?.$el as HTMLElement | undefined;
      const inputElement = fieldElement?.querySelector(
        'input, textarea'
      ) as HTMLElement | null;
      inputElement?.focus();
    });
  }

  focusNextHelpTicketField(field: HelpTicketRenderedField) {
    const fields = this.visibleHelpTicketFields;
    const currentIndex = fields.findIndex(
      (item) => item.fieldKey === field.fieldKey
    );

    if (currentIndex < 0) {
      return;
    }

    const nextField = fields[currentIndex + 1];
    if (!nextField) {
      return;
    }

    this.focusHelpTicketField(nextField);
  }

  getHelpTicketFieldDisplayLabel(field: HelpTicketRenderedField) {
    return this.$i18n(field.label) || '';
  }

  resetHelpTicketFieldValues() {
    Object.keys(this.helpTicketForm).forEach((key) => {
      if (key !== 'ticket_type_id') {
        this.helpTicketForm[key] = '';
      }
    });
    this.resetHelpTicketSkuInfo();
  }

  initHelpTicketFieldValues() {
    this.visibleHelpTicketFields.forEach((field) => {
      if (!(field.fieldKey in this.helpTicketForm)) {
        this.$set(this.helpTicketForm, field.fieldKey, '');
      }
    });
  }

  resetHelpTicketForm() {
    this.helpTicketForm = {
      ticket_type_id: '',
    };
    this.ticketTypeOptions = [];
    this.selectedTicketTypeConfig = null;
    this.helpTicketFieldConfigs = [];
    this.supervisorAssignees = [];
    this.supervisorPassword = '';
    this.createdTicketId = '';
    this.resetHelpTicketSkuInfo();
  }

  getProcessAndStage(path: string): { process: string; stage: string } {
    // 1. 统一转小写，并按 '/' 切分，filter(Boolean) 可以过滤掉首尾或双斜杠产生的空字符串
    const segments = path.toLowerCase().split('/').filter(Boolean);

    // 如果路径为空，直接兜底返回
    if (segments.length === 0) {
      return { process: 'Others', stage: 'Others' };
    }

    // 提取一级路由
    const firstLevel = segments[0];
    // 提取二级路由
    const secondLevel = segments[1] || '';
    // 拼接剩余的其他路由作为判断依据
    const restPath = segments.slice(1).join('/');

    // 2. 核心逻辑分支判断
    if (firstLevel === 'salesoutbound') {
      let stage = 'Others'; // 默认是 Others

      // 根据剩余路由包含的关键字细分 Stage (补全了图片中的完整流向)
      if (restPath.includes('picking') || restPath.includes('outbound')) {
        stage = 'Picking';
      } else if (restPath.includes('checking')) {
        stage = 'Checking';
      } else if (restPath.includes('sorting')) {
        stage = 'Sorting';
      } else if (restPath.includes('packing')) {
        stage = 'Packing';
      } else if (restPath.includes('shipping')) {
        stage = 'Shipping';
      }

      return { process: 'Sales Outbound', stage };
    }

    if (firstLevel === 'inbound') {
      let stage = 'Others'; // 默认是 Others

      // 根据二级路由细分 Stage
      if (secondLevel === 'arriving') {
        stage = 'Arriving';
      } else if (secondLevel === 'boxing') {
        stage = 'Boxing';
      } else if (secondLevel === 'crossdocksorting') {
        stage = 'Cross Dock Sorting';
      } else if (secondLevel === 'qc') {
        stage = 'QC';
      } else if (
        secondLevel === 'standardputaway' ||
        secondLevel === 'massputaway'
      ) {
        stage = 'Putaway';
      }

      return { process: 'Inbound', stage };
    }

    // 3. 精准匹配其他指定的一级路由
    switch (firstLevel) {
      case 'moveinbound':
        return { process: 'MT Inbound', stage: 'Others' };
      case 'rtsoutbound':
        return { process: 'RTS Outbound', stage: 'Others' };
      case 'mtoutbound':
        return { process: 'MT Outbound', stage: 'Others' };
      case 'inventorymanage':
      case 'inventorymap':
        return { process: 'Inventory', stage: 'Others' };

      // 4. 都不匹配时（例如 workticketmanagement / rulecenter / wmsbasic 等），统一兜底
      default:
        return { process: 'Others', stage: 'Others' };
    }
  }

  async handleNeedHelpClick() {
    // 获取当前路由的 path (推荐使用 path，因为 fullPath 带有 query 参数，可能干扰匹配)
    const currentPath = this.$route.path;

    const { process, stage } = this.getProcessAndStage(currentPath);
    const processValue = this.getTicketProcessValue(process);
    const stageValue = this.getTicketStageValue(stage);

    if (!processValue || !stageValue) {
      this.$message.error(
        this.$i18n('Unable to identify current Process and Stage.')
      );
      return;
    }

    this.currentProcess = processValue;
    this.currentStage = stageValue;
    this.currentProcessLabel = process;
    this.currentStageLabel = stage;
    this.resetHelpTicketForm();

    await this.fetchRaiseTicketTypes();
    if (!this.ticketTypeOptions.length) {
      return;
    }

    this.showHelpDialog = true;
  }

  handleHelpDialogCancel() {
    if (this.createTicketLoading) {
      return;
    }
    this.showHelpDialog = false;
  }

  async fetchRaiseTicketTypes() {
    if (!this.currentProcess || !this.currentStage) {
      return;
    }

    this.ticketTypeLoading = true;
    try {
      const res = await searchRaiseTicketType({
        process: this.currentProcess,
        stage: this.currentStage,
      });
      const ticketTypes = this.getRaiseTicketTypesFromResponse(res?.data);
      this.ticketTypeOptions = ticketTypes
        .map((item) => {
          const value = this.getRaiseTicketTypeId(item);
          return {
            value,
            label: this.getRaiseTicketTypeOptionLabel(item, value),
          };
        })
        .filter((item) => Boolean(item.value));
    } catch (error) {
      console.error('[Navbar] Failed to fetch raise ticket types:', error);
      this.ticketTypeOptions = [];
    } finally {
      this.ticketTypeLoading = false;
    }
  }

  getRaiseTicketTypesFromResponse(data: any): RaiseTicketTypeItem[] {
    if (Array.isArray(data)) {
      return data;
    }

    return data?.ticket_types || data?.list || [];
  }

  getRaiseTicketTypeId(item: RaiseTicketTypeItem) {
    return item.ticket_type_id || '';
  }

  getRaiseTicketTypeName(item: RaiseTicketTypeItem) {
    return item.ticket_type_name || item.ticket_type || '';
  }

  getTicketStageLabelByValue(stage: number) {
    const ticketStageEnum =
      safeGet(this.$store.state.enums, 'systemEnums.TicketStage') || {};
    const matchedKey = Object.keys(ticketStageEnum).find(
      (key) => Number(ticketStageEnum[key]) === Number(stage)
    );

    return matchedKey || '';
  }

  getTicketStageDisplayLabel(label: string) {
    switch (normalizeEnumLabel(label)) {
      case 'others':
        return this.$i18n('Others');
      case 'picking':
        return this.$i18n('Picking');
      case 'checking':
        return this.$i18n('Checking');
      case 'sorting':
        return this.$i18n('Sorting');
      case 'packing':
        return this.$i18n('Packing');
      case 'shipping':
        return this.$i18n('Shipping');
      case 'arriving':
        return this.$i18n('Arriving');
      case 'boxing':
        return this.$i18n('Boxing');
      case 'crossdocksorting':
        return this.$i18n('Cross Dock Sorting');
      case 'qc':
        return this.$i18n('QC');
      case 'putaway':
        return this.$i18n('Putaway');
      default:
        return label;
    }
  }

  getRaiseTicketTypeStageLabel(item: RaiseTicketTypeItem) {
    const stage = Number(item.stage ?? this.currentStage);
    const stageLabel =
      (!Number.isNaN(stage) && this.getTicketStageLabelByValue(stage)) ||
      this.currentStageLabel;

    return this.getTicketStageDisplayLabel(stageLabel);
  }

  getRaiseTicketTypeOptionLabel(item: RaiseTicketTypeItem, value: string) {
    const ticketTypeName = this.getRaiseTicketTypeName(item) || value;
    const stageLabel = this.getRaiseTicketTypeStageLabel(item);

    if (!stageLabel) {
      return ticketTypeName;
    }

    return `${stageLabel} - ${ticketTypeName}`;
  }

  async fetchRaiseTicketTypeConfig(ticketTypeId: string) {
    const requestSeq = ++this.ticketTypeConfigRequestSeq;
    this.ticketTypeConfigLoading = true;
    try {
      const res = await getRaiseTicketTypeConfig({
        process: this.currentProcess,
        stage: this.currentStage,
        ticket_type_id: ticketTypeId,
      });

      if (requestSeq !== this.ticketTypeConfigRequestSeq) {
        return;
      }

      const config = this.normalizeRaiseTicketTypeConfig(res?.data);
      this.selectedTicketTypeConfig = config;
      this.helpTicketFieldConfigs = [...(config.field_configs || [])].sort(
        (a, b) => Number(a.sequence_no ?? 0) - Number(b.sequence_no ?? 0)
      );
      this.initHelpTicketFieldValues();
    } catch (error) {
      console.error(
        '[Navbar] Failed to fetch raise ticket type config:',
        error
      );
      if (requestSeq === this.ticketTypeConfigRequestSeq) {
        this.selectedTicketTypeConfig = null;
        this.helpTicketFieldConfigs = [];
      }
    } finally {
      if (requestSeq === this.ticketTypeConfigRequestSeq) {
        this.ticketTypeConfigLoading = false;
      }
    }
  }

  normalizeRaiseTicketTypeConfig(data: RaiseTicketTypeConfig) {
    const fieldConfigs = data?.field_configs || [];
    return {
      ticket_type_id:
        data?.ticket_type_id || this.helpTicketForm.ticket_type_id,
      ticket_type_name: data?.ticket_type_name || '',
      process: Number(data?.process ?? this.currentProcess),
      stage: Number(data?.stage ?? this.currentStage),
      require_supervisor_verification: Number(
        data?.require_supervisor_verification ?? 0
      ),
      supervisor_takeover_required: Number(
        data?.supervisor_takeover_required ?? 0
      ),
      field_configs: fieldConfigs.map((item: any) => ({
        field_type: Number(item.field_type),
        requirement: Number(item.requirement),
        sequence_no: Number(item.sequence_no ?? 0),
      })),
    };
  }

  getSkuImageList(value: unknown) {
    if (Array.isArray(value)) {
      return value.filter(Boolean).map(String);
    }

    if (typeof value === 'string') {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  }

  getHelpTicketSkuImage(data: Record<string, unknown>) {
    return (
      this.getSkuImageList(data.image_list)[0] ||
      this.getSkuImageList(data.sku_images)[0] ||
      this.getSkuImageList(data.images)[0] ||
      String(data.sku_image || data.main_image || '')
    );
  }

  getHelpTicketSkuExpectQty(data: Record<string, unknown>) {
    const expectQty = Number(data.expect_qty ?? data.expected_qty);
    if (Number.isNaN(expectQty)) {
      return undefined;
    }

    return expectQty;
  }

  resetHelpTicketSkuInfo() {
    this.helpTicketSkuRequestSeq += 1;
    this.helpTicketSkuLoading = false;
    this.helpTicketSkuLoadedValue = '';
    this.helpTicketSkuInfo = null;
    this.helpTicketSkuRequestPromise = null;
  }

  async handleHelpTicketScanFieldConfirm(
    field: HelpTicketRenderedField,
    shouldFocusNext = false
  ) {
    const fieldValue = String(this.helpTicketForm[field.fieldKey] || '').trim();
    if (!fieldValue) {
      return;
    }

    const isConfirmed = this.isHelpTicketSkuField(field)
      ? await this.fetchHelpTicketSkuInfo()
      : true;

    if (shouldFocusNext && isConfirmed) {
      this.focusNextHelpTicketField(field);
    }
  }

  async handleHelpTicketScanFieldBlur(
    field: HelpTicketRenderedField,
    event?: FocusEvent
  ) {
    const isFocusMovingInsideForm = this.isHelpTicketFormTarget(
      event?.relatedTarget
    );
    const shouldConfirm =
      isFocusMovingInsideForm || this.isHelpTicketPointerDownInForm;
    this.isHelpTicketPointerDownInForm = false;

    if (!shouldConfirm) {
      return;
    }

    await this.handleHelpTicketScanFieldConfirm(field);
  }

  async fetchHelpTicketSkuInfo() {
    const rawSku = String(this.helpTicketForm.sku_id || '').trim();
    if (!rawSku) {
      this.resetHelpTicketSkuInfo();
      return true;
    }

    if (rawSku === this.helpTicketSkuLoadedValue) {
      return true;
    }

    if (this.helpTicketSkuLoading && this.helpTicketSkuRequestPromise) {
      await this.helpTicketSkuRequestPromise;
      return rawSku === this.helpTicketSkuLoadedValue;
    }

    const requestSeq = ++this.helpTicketSkuRequestSeq;
    this.helpTicketSkuLoading = true;
    let isSkuLoaded = false;
    const requestPromise = (async () => {
      const skuId = String(await skuInputMappingFunction(rawSku)).trim();
      if (!skuId) {
        this.resetHelpTicketSkuInfo();
        return;
      }

      this.helpTicketForm.sku_id = skuId;
      const res = await searchSkuInfoDetail({ sku_id: skuId });
      if (requestSeq !== this.helpTicketSkuRequestSeq) {
        return;
      }

      const skuData = (res?.data || {}) as Record<string, unknown>;
      this.helpTicketSkuInfo = {
        sku_id: String(skuData.sku_id || skuId),
        sku_name: String(skuData.sku_name || skuData.name || ''),
        image: this.getHelpTicketSkuImage(skuData),
        expect_qty: this.getHelpTicketSkuExpectQty(skuData),
      };
      this.helpTicketSkuLoadedValue = skuId;
      isSkuLoaded = true;
    })();

    this.helpTicketSkuRequestPromise = requestPromise;
    try {
      await requestPromise;
      return isSkuLoaded;
    } catch (error) {
      console.error('[Navbar] Failed to fetch SKU info:', error);
      if (requestSeq === this.helpTicketSkuRequestSeq) {
        this.helpTicketSkuInfo = null;
        this.helpTicketSkuLoadedValue = '';
      }
      return false;
    } finally {
      if (requestSeq === this.helpTicketSkuRequestSeq) {
        this.helpTicketSkuLoading = false;
      }
      if (this.helpTicketSkuRequestPromise === requestPromise) {
        this.helpTicketSkuRequestPromise = null;
      }
    }
  }

  validateHelpTicketForm() {
    if (this.ticketTypeLoading || this.ticketTypeConfigLoading) {
      this.$message.warning(this.$i18n('Ticket configuration is loading.'));
      return false;
    }

    if (!this.helpTicketForm.ticket_type_id) {
      this.$message.error(this.$i18n('Please select Ticket Type.'));
      return false;
    }

    if (!this.selectedTicketTypeConfig) {
      this.$message.error(this.$i18n('Ticket configuration is unavailable.'));
      return false;
    }

    const missingField = this.visibleHelpTicketFields.find(
      (field) =>
        field.required &&
        !String(this.helpTicketForm[field.fieldKey] || '').trim()
    );

    if (missingField) {
      this.$message.error(
        `${this.$i18n(missingField.label)} ${this.$i18n('is required.')}`
      );
      return false;
    }

    return true;
  }

  getCreateTicketParams(): CreateGenericTicketRequest {
    const params: CreateGenericTicketRequest & Record<string, unknown> = {
      ticket_type_id: this.helpTicketForm.ticket_type_id,
      process: this.currentProcess,
      stage: this.currentStage,
    };

    this.visibleHelpTicketFields.forEach((field) => {
      const value = String(this.helpTicketForm[field.fieldKey] || '').trim();
      if (value || field.required) {
        params[field.fieldKey] = value;
      }
    });

    return params;
  }

  getCreatedTicketId(data: CreateGenericTicketResponse) {
    return data?.ticket_id || '';
  }

  getCreatedTicketSupervisorAssignees(data: CreateGenericTicketResponse) {
    if (Array.isArray(data?.supervisor_assignees)) {
      return data.supervisor_assignees.filter(Boolean);
    }

    return String(data?.assignee || '')
      .split(',')
      .map((assignee) => assignee.trim())
      .filter(Boolean);
  }

  emitWorkTicketCreated() {
    EventBus.$emit(WORK_TICKET_CREATED_EVENT);
  }

  async handleHelpDialogConfirm() {
    if (this.createTicketLoading) {
      return;
    }

    await this.fetchHelpTicketSkuInfo();

    if (!this.validateHelpTicketForm()) {
      return;
    }

    this.createTicketLoading = true;
    try {
      const res = await createGenericTicket(this.getCreateTicketParams());
      this.createdTicketId = this.getCreatedTicketId(res?.data);
      this.supervisorAssignees = this.getCreatedTicketSupervisorAssignees(
        res?.data
      );
      this.emitWorkTicketCreated();
      this.showHelpDialog = false;

      if (this.needSupervisorVerification) {
        this.showTicketCreatedDialog = true;
        this.startTicketStatusPolling();
        return;
      }

      this.$message.success(this.$i18n('Ticket created successfully.'));
      this.resetHelpTicketForm();
    } catch (error) {
      console.error('[Navbar] Failed to create generic ticket:', error);
    } finally {
      this.createTicketLoading = false;
    }
  }

  async handleTicketCreatedDialogCancel() {
    if (this.needSupervisorVerification && this.createdTicketId) {
      try {
        const actionId = await this.getTicketActionId(this.createdTicketId);
        if (!actionId) {
          return;
        }

        await doTicketAction({
          id: Number(actionId),
          action_code: WORK_TICKET_ACTION_CODE_CANCEL,
          action_source: WORK_TICKET_ACTION_SOURCE_CREATOR_DEVICE,
        });

        this.$message.success(this.$i18n('Ticket canceled successfully.'));
      } catch (error) {
        console.error('[Navbar] Failed to cancel ticket action:', error);
        return;
      }
    }
    this.showTicketCreatedDialog = false;
    this.resetSupervisorVerificationState();
  }

  handleSupervisorConfirmClick() {
    if (!this.createdTicketId) {
      this.$message.error(this.$i18n('Ticket ID is missing.'));
      return;
    }
    this.stopTicketStatusPolling();
    this.showTicketCreatedDialog = false;
    this.supervisorPassword = '';
    this.showSupervisorPasswordDialog = true;
  }

  async handleSupervisorPasswordReject() {
    await this.handleSupervisorPasswordAction(WORK_TICKET_ACTION_CODE_REJECT);
  }

  async handleSupervisorPasswordApprove() {
    await this.handleSupervisorPasswordAction(WORK_TICKET_ACTION_CODE_APPROVE);
  }

  async getTicketActionId(ticketId: string) {
    const actionListRes = await searchTicketActionList({
      ticket_id: ticketId,
    });

    const actionId = String(
      actionListRes?.data?.action_list?.[0]?.id ?? ''
    ).trim();

    return actionId;
  }

  async handleSupervisorPasswordAction(actionCode: number) {
    if (this.supervisorPasswordLoading) {
      return;
    }

    if (!this.createdTicketId) {
      this.$message.error(this.$i18n('Ticket ID is missing.'));
      return;
    }

    const password = this.supervisorPassword.trim();
    if (!password) {
      this.$message.error(this.$i18n('Please input supervisor password.'));
      return;
    }

    this.supervisorPasswordLoading = true;
    try {
      await checkSupervisorPassword({
        ticket_id: this.createdTicketId,
        password,
      });
      const actionId = await this.getTicketActionId(this.createdTicketId);
      if (!actionId) {
        return;
      }

      await doTicketAction({
        id: Number(actionId),
        action_code: actionCode,
        action_source: WORK_TICKET_ACTION_SOURCE_CREATOR_DEVICE,
      });
      if (actionCode === WORK_TICKET_ACTION_CODE_APPROVE) {
        this.$message.success(this.$i18n('Ticket Approved.'));
      } else {
        this.$message.success(this.$i18n('Ticket Rejected.'));
      }
      this.showSupervisorPasswordDialog = false;
      this.resetSupervisorVerificationState();
    } catch (error) {
      console.error('[Navbar] Failed to handle supervisor password:', error);
    } finally {
      this.supervisorPasswordLoading = false;
    }
  }

  resetSupervisorVerificationState() {
    this.stopTicketStatusPolling();
    this.supervisorPassword = '';
    this.createdTicketId = '';
    this.supervisorAssignees = [];
    this.selectedTicketTypeConfig = null;
    this.helpTicketFieldConfigs = [];
    this.helpTicketForm.ticket_type_id = '';
  }

  startTicketStatusPolling() {
    this.stopTicketStatusPolling();
    this.ticketStatusPollingTimer = setInterval(async () => {
      if (!this.createdTicketId || !this.showTicketCreatedDialog) {
        this.stopTicketStatusPolling();
        return;
      }

      try {
        const res = await searchTicketDetail({
          ticket_id: this.createdTicketId,
        });
        const ticketStatus = res?.data?.ticket_status;

        if (ticketStatus === TICKET_STATUS_DONE) {
          this.showTicketCreatedDialog = false;
          this.resetSupervisorVerificationState();
          this.$message.success(
            this.$i18n('Ticket has been approved by supervisor.')
          );
        } else if (ticketStatus === TICKET_STATUS_REJECTED) {
          this.showTicketCreatedDialog = false;
          this.resetSupervisorVerificationState();
          this.$message.warning(
            this.$i18n('Ticket has been rejected by supervisor.')
          );
        } else if (ticketStatus === TICKET_STATUS_CANCEL) {
          this.showTicketCreatedDialog = false;
          this.resetSupervisorVerificationState();
          this.$message.info(this.$i18n('Ticket has been canceled.'));
        }
      } catch (error) {
        console.error('[Navbar] Failed to poll ticket status:', error);
      }
    }, TICKET_STATUS_POLL_INTERVAL);
  }

  stopTicketStatusPolling() {
    if (this.ticketStatusPollingTimer !== null) {
      clearInterval(this.ticketStatusPollingTimer);
      this.ticketStatusPollingTimer = null;
    }
  }

  @Watch('helpTicketForm.ticket_type_id')
  async handleHelpTicketTypeChanged(ticketTypeId: string) {
    this.resetHelpTicketFieldValues();
    this.selectedTicketTypeConfig = null;
    this.helpTicketFieldConfigs = [];

    if (!ticketTypeId) {
      this.ticketTypeConfigRequestSeq += 1;
      this.ticketTypeConfigLoading = false;
      return;
    }

    await this.fetchRaiseTicketTypeConfig(ticketTypeId);
  }

  @Watch('helpTicketForm.sku_id')
  handleHelpTicketSkuChanged(skuId: string) {
    const normalizedSkuId = String(skuId || '').trim();
    if (!normalizedSkuId || normalizedSkuId !== this.helpTicketSkuLoadedValue) {
      this.helpTicketSkuInfo = null;
      this.helpTicketSkuLoadedValue = '';
    }
  }

  handleBeforeUnload(e: BeforeUnloadEvent) {
    if (this.isVerificationInProgress) {
      e.preventDefault();
    }
  }

  handleStayOnPage() {
    this.showLeaveWarningDialog = false;
    this.pendingAction = null;
  }

  handleLeaveAnyway() {
    this.showLeaveWarningDialog = false;
    this.showTicketCreatedDialog = false;
    this.showSupervisorPasswordDialog = false;
    this.resetSupervisorVerificationState();

    if (this.pendingAction) {
      this.pendingAction();
      this.pendingAction = null;
    }
  }

  @Watch('$route', { immediate: true })
  onAfterChanged(to: any) {
    this.initNavBar(to);
    this.initSystemTitle(to);
  }

  @Watch('route.to', { immediate: true })
  handleRouteChanged(to: any) {
    if (to?.path?.includes('hubLinehaulTrips')) {
      this.initNavBar(to);
    }
  }

  // 检查并更新 Support Panel 显示状态
  checkSupportPanel() {
    // 和live红框展示逻辑一致
    // const supportRoles = [
    //   'FE',
    //   'BE',
    //   'QA',
    //   'Android',
    //   'Algo',
    //   'iOS',
    //   'DE',
    //   'PJM',
    //   'HRBP',
    //   'FPM',
    //   'PD',
    //   'PS',
    // ];
    // const role = getCookie('ssc_user_role') || '';
    // this.showSupportPanelValue =
    //   supportRoles.includes(role) || getEnv() !== 'live';
    this.showSupportPanelValue = getEnv() !== 'live';
  }

  showSupportPanelDrawer() {
    this.switchPanelVisible = true;
    reportEvent('click_support_panel');
  }

  async created() {
    try {
      this.backendLanguages = await this.$request.get(
        '/api/v2/apps/system/user/get_lang_setting'
      );
      this.languages = this.backendLanguages.data.optional_lange_list.map(
        (item: any) => {
          return {
            label: item.desc,
            value: item.lange,
          };
        }
      );
      this.lang = this.languages.filter((item: any) => {
        return item.value === this.backendLanguages.data.default;
      })[0].label;
      this.initSystemTitle(this.$router.currentRoute);
    } catch (e) {
      console.log('error', e);
    }
  }

  async mounted() {
    this.enableTobLogin = await checkLoginSwitch();
    this.checkSupportPanel();
    const routeInfo = this.$route;
    if (routeInfo.hash === '#useSupportPanel') {
      this.showSupportPanelDrawer();
    }

    // 监听浏览器的刷新和关闭页签事件
    window.addEventListener('beforeunload', this.handleBeforeUnload);

    // 监听 Vue Router 内部页面跳转
    this.unregisterRouterGuard = this.$router.beforeEach(
      (to: any, from: any, next: any) => {
        if (this.isVerificationInProgress) {
          this.pendingAction = () => {
            this.$router.push(to.fullPath || to.path).catch((error: any) => {
              if (error?.name !== 'NavigationDuplicated') {
                console.error('[Navbar] Failed to continue navigation:', error);
              }
            });
          };
          this.showLeaveWarningDialog = true;
          next(false); // 拦截当前跳转，等待弹窗确认
        } else {
          next();
        }
      }
    );
  }

  beforeDestroy() {
    // 销毁监听器，防止内存泄漏
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    if (this.unregisterRouterGuard) {
      this.unregisterRouterGuard();
    }
    this.stopTicketStatusPolling();
  }

  handleFullscreen() {
    if (!document.fullscreenElement) {
      // 进入全屏
      const element = document.documentElement;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        (element as any).webkitRequestFullscreen();
      } else if ((element as any).msRequestFullscreen) {
        (element as any).msRequestFullscreen();
      }
    } else {
      // 退出全屏
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        (document as any).msExitFullscreen();
      }
    }
  }

  // 跳转到 WFM
  handleSystemOutToWFM() {
    location.href = getWFMSystemUrl();
  }

  async handleChangePassword() {
    if (this.enableTobLogin) {
      window.location.href = await getTobURL('/user/password', [
        'default_region',
        'google_login_redirect',
      ]);
      return;
    }
    this.changePasswordDialogVisible = true;
  }

  // 登出
  async handleLogoutClick() {
    if (this.isVerificationInProgress) {
      this.pendingAction = () => this.executeLogout();
      this.showLeaveWarningDialog = true;
      return;
    }
    await this.executeLogout();
  }

  async executeLogout() {
    let canLeave = true;
    try {
      canLeave = await LeavePageWarningService.checkBeforeLeave();
    } catch (error) {
      console.error('[Navbar] Failed to check leave warning:', error);
      // 预警检查失败时，允许用户正常登出
    }
    if (!canLeave) {
      return;
    }

    await logOut();
    this.$store.commit('clearUserInfo');
    this.$router.push({
      name: 'tobLogin',
      query: {
        redirect_url: encodeURI(location.href),
      },
    });
  }

  async handleLanguage(item: any) {
    if (this.isVerificationInProgress) {
      this.pendingAction = () => this.executeLanguageChange(item);
      this.showLeaveWarningDialog = true;
      return;
    }
    await this.executeLanguageChange(item);
  }

  async executeLanguageChange(item: any) {
    try {
      await this.$request.post('/api/v2/apps/system/user/set_user_setting', {
        name: 'lang',
        value: item.value,
      });
      this.lang = item.label;
      this.setLocale(item.value);

      this.initSystemTitle(this.$router.currentRoute);
      this.getTranslateJSon(
        this.tspLanguageMap.get(getCurrentLocale()) || getCurrentLocale()
      );
      this.$nextTick(() => {
        TranslateReport.resetData();
      });
      if (
        this.$route.matched[0].path === '/dashboard' ||
        this.$route.matched[0].path === '/regionOverview'
      ) {
        const iframeDom: any = document.getElementById('dashboard-iframe');
        iframeDom.contentWindow.postMessage({ lang: item.value });
      }
    } catch (e) {
      console.log('error', e);
    } finally {
      location.reload();
    }
  }

  handleItemClick(e: Event) {
    console.log(e);
  }

  openLink(name: string) {
    if (name === this.$route.name) {
      return;
    }
    this.$router.push({ name }).catch((e: any) => {
      //有些空页面继续重定向回来会报错，属于正常业务逻辑
      console.log(e);
    });
  }

  initNavBar(to: any) {
    this.navList = [];
    if (to.meta && to.meta.navList) {
      this.navList = [
        ...to.meta.navList,
        {
          routeName: to.name,
          title: to.meta.title,
        },
      ];
    } else {
      if (to.matched && to.matched.length > 0) {
        to.matched.forEach((item: any) => {
          this.navList.push({
            routeName: item.name,
            title: item.meta.title,
          });
        });
      }
    }
    this.navList = this.formatNavList(this.navList, to);
  }

  // 用于生成wms网页title
  initSystemTitle(to: any) {
    let systemTitle: any = [];
    const isMenuHideRouteByName = (name: string) => {
      const routes = this.$router.getRoutes();
      const route = routes.find((route: any) => route.name === name);
      return route?.meta?.menuHide && !route?.name?.startsWith?.('PC.');
    };
    // 去掉不显示在菜单栏的路由，保留tab的路由
    const _popMenuHideTitle = () => {
      if (
        systemTitle.length > 2 &&
        isMenuHideRouteByName(
          (systemTitle[systemTitle.length - 1] as any).routeName
        )
      ) {
        systemTitle.pop();
        _popMenuHideTitle();
      }
    };
    if (to.meta && to.meta.navList) {
      // route配置了navList的情况，取navList和当前route的title组合后处理
      systemTitle = [
        ...to.meta.navList,
        {
          routeName: to.name,
          title: to.meta.title,
        },
      ];

      _popMenuHideTitle();
    } else {
      if (to.matched && to.matched.length > 0) {
        // route没有配置navList的情况，取to.matched后处理
        to.matched.forEach((item: any, index: any) => {
          systemTitle.push({
            routeName: item.name,
            title: item.meta.title,
          });
        });
        _popMenuHideTitle();
      }
    }
    // 取最后两项，翻译后，拼接WMS字符串
    systemTitle = systemTitle.splice(-2);
    const title = [];
    systemTitle.forEach(
      (item: any) => item.title && title.push(this.$i18n(item.title))
    );
    title.push(this.$i18n('WMS'));
    document.title = title.join('-');
  }

  /**
   *  支持 ${key} 查找 to.params[key] 的方式替换 title 的 ${key} 部分
   *  例: '${key} ${key2} title' => `${to.params[key]} ${to.params[key2]} title`
   */
  tranParamTitle(title: string, to: Route) {
    let newTitle = title;
    const matchReg = /\$\{(\w+)\}/g;
    const matchArr = title.matchAll(matchReg);
    if (to.params) {
      for (const [fullMatchKey, key] of matchArr) {
        // 默认取 route.param 中匹配的参数，没有匹配替换为 ''
        const paramValue = to.params[key] || '';
        newTitle = newTitle.replace(fullMatchKey, paramValue);
      }
    }
    return newTitle;
  }

  // 转换 navlist title 字段
  formatNavList(navList: any, to: Route) {
    return this.navList.map(({ title = '', ...res }: any) => ({
      title: this.tranParamTitle(title, to),
      ...res,
    }));
  }
}
</script>

<style scoped lang="scss">
.navbar-wrapper {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  padding-right: 24px;
  height: 56px;
  background: #ffffff;
}

.navbar-actions {
  display: flex;
  align-items: center;
}

.navbar-breadcrumb {
  max-width: 560px;
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;

  ::v-deep .ssc-breadcrumb-item-link {
    white-space: nowrap;
    cursor: pointer;
  }
}

.help-button {
  margin-right: 16px;
}

.help-dialog-message {
  line-height: 22px;
  color: #303133;
}

.help-dialog-assignees {
  color: #3274f7;
}

.help-ticket-form {
  padding: 4px 16px 0;

  ::v-deep .ssc-form-item {
    margin-bottom: 16px;
  }

  ::v-deep .ssc-form-item-content {
    flex-direction: column;
    width: 100%;
  }

  ::v-deep .ssc-form-item-label {
    font-size: 14px;
    line-height: 20px;
    color: #7e8692;
  }

  ::v-deep {
    .ssc-select,
    .ssc-input,
    .ssc-textarea-container,
    .ssc-textarea {
      width: 100%;
    }
  }
}

.help-ticket-control {
  width: 100%;
  max-width: 100%;
}

.help-ticket-field-label {
  display: inline-flex;
  align-items: center;
}

.help-ticket-field-help-icon {
  margin-left: 4px;
  width: 14px;
  height: 14px;
  color: #7e8692;
  vertical-align: -2px;
}

.help-ticket-sku-card {
  display: flex;
  margin-top: 8px;
  border: 1px solid #edf0f5;
  border-radius: 2px;
  padding: 8px;
  width: 100%;
  min-height: 72px;
  background: #f8f9fd;
}

.help-ticket-sku-image {
  flex: 0 0 56px;
  overflow: hidden;
  margin-right: 8px;
  border-radius: 2px;
  width: 56px;
  height: 56px;
  background: #edf0f5;

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.help-ticket-sku-detail {
  overflow: hidden;
  min-width: 0;
  color: #303133;
  line-height: 18px;
}

.help-ticket-sku-id,
.help-ticket-sku-name,
.help-ticket-sku-qty {
  overflow: hidden;
  text-overflow: ellipsis;
}

.help-ticket-sku-id {
  white-space: nowrap;
}

.help-ticket-sku-name {
  margin-top: 2px;
  max-height: 36px;
  color: #606266;
}

.help-ticket-sku-qty {
  margin-top: 2px;
  white-space: nowrap;
  color: #606266;
}

.help-ticket-scan-control {
  ::v-deep .ssc-input-slot-prefix {
    padding-right: 4px;
    padding-left: 8px;
  }

  ::v-deep .ssc-input-slot-prefix::after {
    width: 0;
  }
}

.help-ticket-scan-icon {
  display: block;
  width: 14px;
  height: 14px;
}

.help-ticket-remark-control {
  width: 100%;
}

.leave-warning-message {
  line-height: 22px;
  color: #303133;
}

.supervisor-password-dialog {
  padding: 2px 0 8px;
}

.supervisor-password-label {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 20px;
  color: #7e8692;
}

.supervisor-password-required {
  margin-right: 4px;
  color: #ff4d4f;
}

.supervisor-password-input,
.supervisor-password-input ::v-deep .ssc-input {
  width: 100%;
}

.supervisor-password-footer {
  display: flex;
  justify-content: flex-end;
}

.supervisor-password-reject {
  margin-right: 16px;
  border-color: #ff4d4f;
  color: #ff4d4f;
  background-color: #ffffff;
}

.supervisor-password-reject:hover,
.supervisor-password-reject:focus {
  border-color: #ff4d4f;
  color: #ff4d4f;
  background-color: #ffffff;
}

.supervisor-password-approve {
  border-color: #00b578;
  color: #00b578;
  background-color: #ffffff;
}

.supervisor-password-approve:hover,
.supervisor-password-approve:focus {
  border-color: #00b578;
  color: #00b578;
  background-color: #ffffff;
}

::v-deep .supervisor-password-dialog-container {
  .ssc-dialog-body {
    padding-bottom: 18px;
  }

  .ssc-dialog-footer {
    padding: 0;
  }
}

.avatar {
  overflow: hidden;
  margin-right: 8px;
  margin-left: 20px;
  border-radius: 50%;
  width: 32px;
  height: 32px;

  img {
    width: 32px;
    height: 32px;
  }
}

.fullscreen-button {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  cursor: pointer;
}

.fullscreen-button:hover {
  background-color: rgba(0, 0, 0, 4%);
}

.fullscreen-button svg {
  width: 16px;
}

.system-out-button {
  display: flex;
  align-items: center;
  margin: 6px 16px 6px 0;
  border-radius: 22px;
  padding-right: 16px;
  height: 44px;
  font-size: 12px;
  user-select: none;
  cursor: pointer;
}

.system-out-button:hover {
  background-color: #f8f9fd;
}

.system-out-icon {
  margin: 0 4px 0 8px;
  width: 16px !important;
  height: 16px !important;
}

.switch-panel-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
  width: 32px;
  height: 32px;
  cursor: pointer;
}

.navbar-icon-small {
  width: 16px;
  height: 16px;
}

.navbar-chevron-icon {
  width: 16px;
}

.mr10 {
  margin-right: 10px;
}
</style>
